import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * The technician's local database.
 *
 * The rule the whole field app is built on: **it never reads from the network to render a
 * screen.** It reads from here. The network's only jobs are to refill this database and to
 * drain the outbox.
 *
 * That is the opposite of a normal web app, and it has to be decided up front — retrofitting
 * offline onto a fetch-on-render app means rewriting every screen. A technician in a
 * basement, a mechanical room or an underground garage sees exactly the same app as one
 * standing in the street; the only difference is a sync indicator.
 *
 * **The unit here is the stop, not the job.** That is the one structural change `FIELD.md`
 * forced: the client's unit is the visit — with its job, customer and address denormalised
 * onto it — because a job needing three trips is three different days, three different crews
 * and three different sets of photographs, and a `LocalJob` cannot say which of them the
 * technician is standing in.
 */

/** The version. Bumped whenever a store is added, because `upgrade` is the only migration. */
const DB_NAME = 'monrdv-field';
const DB_VERSION = 2;

// ---------------------------------------------------------------------------
// The stop
// ---------------------------------------------------------------------------

/** Where the technician is on this stop. Derived on the server; mirrored here. */
export type LocalStopState =
  | 'scheduled'
  | 'en_route'
  | 'delayed'
  | 'arrived'
  | 'working'
  | 'done'
  | 'not_done'
  | 'cancelled';

export type LocalCapture = {
  id: string;
  kind: string;
  caption: string | null;
  contentType: string;
  capturedAt: string | null;
  transcript: string | null;
  takenBy: string | null;
  /** Set on captures that exist only on this phone. Drives the "not sent" mark. */
  pending?: boolean;
};

export type LocalReading = {
  id: string;
  key: string;
  value: number;
  unit: string | null;
  phase: 'before' | 'after' | 'single';
  locationLabel: string | null;
  outOfSpec: boolean;
  derived: boolean;
  attachmentId: string | null;
  takenAt: string | null;
  pending?: boolean;
};

export type LocalStop = {
  id: string;
  number: string | null;
  sequence: number;
  status: string;
  state: LocalStopState;

  /**
   * The window, and the two fields that decide how to say it.
   *
   * A visit promised as "mercredi AM" is a period, not 08:00. Rendering `startsAt` without
   * checking `precision` tells the customer something nobody promised and makes the technician
   * late by definition.
   */
  startsAt: string | null;
  endsAt: string | null;
  precision: 'exact' | 'period' | 'day';
  period: 'am' | 'pm' | 'day' | 'evening' | 'week' | null;

  enRouteAt: string | null;
  etaAt: string | null;
  arrivedAt: string | null;
  departedAt: string | null;
  completedAt: string | null;
  arrivedFix: { latitude: number; longitude: number; accuracyM: number | null } | null;

  outcome: 'complete' | 'partial' | 'return_needed' | null;
  reasonId: string | null;
  outcomeNote: string | null;
  note: string | null;

  siteContactName: string | null;
  siteContactPhone: string | null;
  poNumber: string | null;

  job: {
    id: string;
    number: string;
    title: string | null;
    description: string | null;
    internalNote: string | null;
    balance: string;
  };
  stage: { id: string; name: unknown; kind: string; color: string };
  customer: {
    id: string;
    name: string;
    organization: string | null;
    /** The language anything shown to the customer renders in. Not the technician's. */
    locale: 'fr' | 'en';
    parentId: string | null;
  };
  address: {
    id: string;
    line1: string;
    line2: string | null;
    /** 614 addresses carry it inside line1. On a 63-unit block it *is* the address. */
    unit: string | null;
    city: string;
    region: string | null;
    postalCode: string | null;
    /** Gate codes, "ring twice", where to park. The most useful field on site. */
    note: string | null;
    latitude: number | null;
    longitude: number | null;
    placeKey: string;
    buildingKey: string;
  } | null;
  services: Array<{ id: string; name: unknown; color: string; quantity: number; defaultDurationMinutes: number | null }>;
  crew: Array<{ id: string; name: string; initials: string; color: string | null }>;
  assigneeId: string | null;
  phones: Array<{ raw: string; normalized: string; kind: string }>;

  captures: LocalCapture[];
  readings: LocalReading[];
  parts: Array<{ id: string; productId: string | null; label: string | null; scannedCode: string | null; quantity: number }>;
  assetIds: string[];

  /** True while a local change to this stop has not been acknowledged. */
  pending?: boolean;
};

/**
 * A dwelling, with everything that outlives a visit to it.
 *
 * The reason the address identity work matters: 338 imported addresses carry more than one
 * customer, and the ducts are the same ducts.
 */
export type LocalPlace = {
  placeKey: string;
  history: Array<{
    appointmentId: string;
    at: string | null;
    status: string;
    outcome: string | null;
    jobNumber: string;
    customerName: string;
    services: unknown[];
    captureCount: number;
  }>;
  assets: Array<{
    id: string;
    assetTypeId: string | null;
    brand: string | null;
    model: string | null;
    serial: string | null;
    locationLabel: string | null;
    installedOn: string | null;
    note: string | null;
    nameplateAttachmentId: string | null;
    requirements: Array<{ requirementId: string; note: string | null }>;
  }>;
  hazards: Array<{
    id: string;
    assetId: string | null;
    target: 'technician' | 'customer';
    severityId: string;
    categoryId: string | null;
    note: string;
    attachmentId: string | null;
    acknowledgedAt: string | null;
    acknowledgedName: string | null;
    outcome: string | null;
    reportedBy: string | null;
    at: string | null;
  }>;
  access: Array<{
    requirementId: string;
    note: string | null;
    contactName: string | null;
    contactPhone: string | null;
    confirmedAt: string | null;
  }>;
  /**
   * What was photographed here before, as an index rather than as bytes.
   *
   * Five years of before-and-after shots would be tens of megabytes per dwelling, which is
   * exactly what the bounded working set exists to avoid. So the client holds the list — and
   * can therefore say *twelve photographs from the 2023 visit* with no signal — and fetches a
   * thumbnail only when it has one. The service worker caches those on the way past, so a
   * photograph looked at in the street stays viewable in the basement.
   */
  captures: Array<{
    id: string;
    kind: string;
    caption: string | null;
    contentType: string;
    capturedAt: string | null;
    appointmentId: string | null;
  }>;
};

/** The block. Never to be read as one household: the largest here holds 82 customers. */
export type LocalBuilding = {
  buildingKey: string;
  city: string | null;
  customerCount: number;
  notes: string[];
};

/**
 * Something this technician has to be told, and possibly agree to.
 *
 * Held locally for the same reason as everything else: a blocking notice has to be able to
 * stop the app in a basement, and the acknowledgement has to be able to leave it later. The
 * client never invents one — `acknowledgedAt` is set optimistically and confirmed by the next
 * working set.
 */
export type LocalNotice = {
  noticeId: string;
  versionId: string;
  key: string;
  kind: string;
  version: number;
  title: unknown;
  body: unknown;
  enforcement: 'blocking' | 'acknowledge' | 'informational';
  publicUrl: string | null;
  publishedAt: string | null;
  acknowledgedAt: string | null;
};

export type LocalCustomer = {
  id: string;
  displayName: string;
  organization: string | null;
  locale: 'fr' | 'en';
  note: string | null;
  parentId: string | null;
  phones: Array<{ raw: string; normalized: string; kind: string }>;
  emails: string[];
};

export type LocalInvoice = {
  id: string;
  jobId: string;
  number: string;
  status: string;
  total: string;
  paidTotal: string;
  balance: string;
  dueOn: string | null;
  issuedOn: string | null;
};

export type LocalMessage = {
  id: string;
  threadId: string;
  jobId: string | null;
  customerId: string | null;
  authorKind: 'staff' | 'customer' | 'system';
  authorName: string | null;
  body: string | null;
  transport: string | null;
  sentAt: string;
  pending?: boolean;
};

export type LocalTimeEntry = {
  id: string;
  jobId: string | null;
  appointmentId: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationMinutes: number;
  note: string | null;
  pending?: boolean;
};

/**
 * The vocabulary the business declared.
 *
 * Held whole because nothing on the stop may need the network to render — and that includes
 * the words on its own buttons. It is small: a few dozen rows per list.
 */
export type LocalReference = {
  key:
    | 'services'
    | 'products'
    | 'taxRates'
    | 'reasons'
    | 'captureKinds'
    | 'captureRequirements'
    | 'assetTypes'
    | 'accessRequirements'
    | 'hazardSeverities'
    | 'hazardCategories'
    | 'defaultParts'
    | 'coreCaptureKinds'
    | 'crew'
    | 'settings'
    | 'notices'
    | 'session';
  value: unknown;
};

// ---------------------------------------------------------------------------
// Writing
// ---------------------------------------------------------------------------

/**
 * A capture waiting to leave the phone, held as a Blob.
 *
 * Kept out of the outbox because it is not a request: it is bytes, uploaded in chunks and
 * resumable, and the outbox's ordered drain would stall behind a 400 KB photograph on one bar
 * of LTE. What *does* go in the outbox is whatever refers to it — a reading, a hazard — with
 * `dependsOn` pointing at the entry that files it.
 */
export type PendingMedia = {
  id: string;
  appointmentId: string;
  kind: string;
  blob: Blob;
  contentType: string;
  fileName: string;
  /** SHA-256, computed on the device. The server refuses bytes that disagree. */
  checksum: string;
  caption: string | null;
  capturedAt: string;
  position: { latitude: number; longitude: number; accuracyM: number | null } | null;
  durationSeconds: number | null;
  assetId: string | null;
  /** The server's upload session, once started. Null before the first attempt. */
  uploadId: string | null;
  /** How many bytes the server holds. The resume point. */
  offset: number;
  /** The real attachment id, once the upload completed. */
  attachmentId: string | null;
  attempts: number;
  lastAttemptAt: string | null;
  rejectedReason: string | null;
  createdAt: string;
};

/**
 * One queued change.
 *
 * The `entity` union grew with the field client, and each member is a thing that happened at a
 * stop rather than a table that changed. That is deliberate: an outbox entry has to survive
 * being replayed an hour later against a server that has moved on, and "the technician arrived"
 * replays sensibly where "set arrived_at = 08:41" does not.
 */
export type OutboxEntry = {
  id: string;
  /** Generated on the device. The server returns the original result on a replay. */
  idempotencyKey: string;
  entity:
    | 'transition'
    | 'reading'
    | 'hazard'
    | 'asset'
    | 'access'
    | 'part'
    | 'request'
    | 'prospect'
    | 'quote'
    | 'invoice'
    | 'payment'
    | 'time_entry'
    | 'note'
    | 'transcript'
    | 'message'
    | 'notice'
    | 'customer';
  operation: 'create' | 'update' | 'complete';
  /** Relative API path and body. Kept literal so a queued call needs no re-derivation. */
  path: string;
  method: 'POST' | 'PATCH' | 'PUT';
  body: unknown;
  /** Must be sent after this entry: a reading whose instrument photograph is still uploading. */
  dependsOn: string | null;
  /**
   * A media record this entry needs uploaded first.
   *
   * Different from `dependsOn`, which points at another outbox entry. A capture is not an
   * outbox entry, and the id it will eventually have is the server's — so the entry names the
   * local media row and its body is patched with the real attachment id at send time.
   */
  awaitsMedia?: { mediaId: string; field: string } | null;
  /** Local record this touches, so the UI can show a pending mark on the right row. */
  targetId: string | null;
  /** Shown in the pending list, in French, so the queue is legible to its owner. */
  label: string;
  attempts: number;
  lastAttemptAt: string | null;
  rejectedReason: string | null;
  createdAt: string;
};

export type SyncMeta = {
  key:
    | 'lastSyncedAt'
    | 'workingSetFrom'
    | 'workingSetTo'
    | 'userId'
    | 'tenantId'
    | 'tenantSlug'
    | 'capabilities'
    | 'storagePersisted'
    | 'lockedAt'
    | 'unlockedAt'
    | 'shiftStartedAt'
    | 'deviceLabel';
  value: string;
};

interface FieldDB extends DBSchema {
  stops: { key: string; value: LocalStop; indexes: { byStart: string } };
  places: { key: string; value: LocalPlace };
  buildings: { key: string; value: LocalBuilding };
  customers: { key: string; value: LocalCustomer };
  invoices: { key: string; value: LocalInvoice; indexes: { byJob: string } };
  messages: { key: string; value: LocalMessage; indexes: { byThread: string; byJob: string } };
  timeEntries: { key: string; value: LocalTimeEntry; indexes: { byAppointment: string } };
  reference: { key: string; value: LocalReference };
  outbox: { key: string; value: OutboxEntry; indexes: { byCreated: string } };
  media: { key: string; value: PendingMedia; indexes: { byAppointment: string } };
  meta: { key: string; value: SyncMeta };
}

let dbPromise: Promise<IDBPDatabase<FieldDB>> | undefined;

export function db(): Promise<IDBPDatabase<FieldDB>> {
  dbPromise ??= openDB<FieldDB>(DB_NAME, DB_VERSION, {
    /*
     * The upgrade is destructive for data and deliberately so.
     *
     * Version 1 held `jobs`; version 2 holds `stops`, which is a different unit with a
     * different key. There is no sensible conversion, and the cost of throwing it away is one
     * sync — the working set is a cache of the server, not a record. The **outbox and the
     * media are never dropped**, because those are the one thing here that exists nowhere
     * else.
     */
    upgrade(database, oldVersion) {
      if (oldVersion < 2) {
        // Named as plain strings: `jobs` is version 1's store and no longer part of the typed
        // schema, so the typed overload cannot describe dropping it.
        for (const name of ['jobs', 'customers', 'messages', 'timeEntries', 'reference']) {
          const legacy = database as unknown as IDBDatabase;
          if (legacy.objectStoreNames.contains(name)) legacy.deleteObjectStore(name);
        }
      }

      if (!database.objectStoreNames.contains('stops')) {
        const stops = database.createObjectStore('stops', { keyPath: 'id' });
        stops.createIndex('byStart', 'startsAt');
      }
      if (!database.objectStoreNames.contains('places')) {
        database.createObjectStore('places', { keyPath: 'placeKey' });
      }
      if (!database.objectStoreNames.contains('buildings')) {
        database.createObjectStore('buildings', { keyPath: 'buildingKey' });
      }
      if (!database.objectStoreNames.contains('customers')) {
        database.createObjectStore('customers', { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains('invoices')) {
        const invoices = database.createObjectStore('invoices', { keyPath: 'id' });
        invoices.createIndex('byJob', 'jobId');
      }
      if (!database.objectStoreNames.contains('messages')) {
        const messages = database.createObjectStore('messages', { keyPath: 'id' });
        messages.createIndex('byThread', 'threadId');
        messages.createIndex('byJob', 'jobId');
      }
      if (!database.objectStoreNames.contains('timeEntries')) {
        const time = database.createObjectStore('timeEntries', { keyPath: 'id' });
        time.createIndex('byAppointment', 'appointmentId');
      }
      if (!database.objectStoreNames.contains('reference')) {
        database.createObjectStore('reference', { keyPath: 'key' });
      }
      if (!database.objectStoreNames.contains('outbox')) {
        const outbox = database.createObjectStore('outbox', { keyPath: 'id' });
        outbox.createIndex('byCreated', 'createdAt');
      }
      if (!database.objectStoreNames.contains('media')) {
        const media = database.createObjectStore('media', { keyPath: 'id' });
        media.createIndex('byAppointment', 'appointmentId');
      }
      if (!database.objectStoreNames.contains('meta')) {
        database.createObjectStore('meta', { keyPath: 'key' });
      }
    },
    blocked() {
      // Another tab is holding an old version open. Rare on a phone, but it would leave the
      // app on a stale schema silently.
      console.warn('[field] database upgrade blocked by another open tab');
    },
  });
  return dbPromise;
}

/**
 * Ask the browser not to evict our data.
 *
 * Without this, storage pressure can silently clear the working set — and with it a
 * technician's unsent hours and photos. That is the worst failure this app can have, so if
 * the request is refused the app says so rather than pretending everything is safe.
 */
export async function requestPersistence(): Promise<boolean> {
  if (!('storage' in navigator) || !navigator.storage.persist) return false;
  try {
    const already = await navigator.storage.persisted();
    const granted = already || (await navigator.storage.persist());
    await setMeta('storagePersisted', String(granted));
    return granted;
  } catch {
    return false;
  }
}

/** How much room is left, so the app can warn before a capture fails. */
export async function storageEstimate(): Promise<{ usedMb: number; quotaMb: number } | null> {
  if (!('storage' in navigator) || !navigator.storage.estimate) return null;
  try {
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();
    return { usedMb: Math.round(usage / 1048576), quotaMb: Math.round(quota / 1048576) };
  } catch {
    return null;
  }
}

export async function getMeta(key: SyncMeta['key']): Promise<string | null> {
  const row = await (await db()).get('meta', key);
  return row?.value ?? null;
}

export async function setMeta(key: SyncMeta['key'], value: string): Promise<void> {
  await (await db()).put('meta', { key, value });
}

/** Everything the business declared, read back as a typed list. */
export async function reference<T>(key: LocalReference['key'], fallback: T): Promise<T> {
  const row = await (await db()).get('reference', key);
  return (row?.value as T) ?? fallback;
}

export const STORES = [
  'stops',
  'places',
  'buildings',
  'customers',
  'invoices',
  'messages',
  'timeEntries',
  'reference',
  'outbox',
  'media',
  'meta',
] as const;

/**
 * Wipe everything local.
 *
 * Used on sign-out, when switching business, and on a revoked device. Deliberately refuses
 * while the outbox still holds work, because signing out should never be the thing that loses
 * a day of unsent hours.
 *
 * **A revoke overrides that refusal.** Two weeks of customers' names, telephone numbers,
 * addresses and gate codes sit on a phone that lives in a van; a lost phone's unsent hours are
 * re-entered, and its customer list is not negotiable.
 */
export async function clearLocalData(options: { force?: boolean } = {}): Promise<{ cleared: boolean; pending: number }> {
  const database = await db();
  const pending = (await database.count('outbox')) + (await database.count('media'));
  if (pending > 0 && !options.force) return { cleared: false, pending };

  const tx = database.transaction(STORES, 'readwrite');
  await Promise.all([...STORES.map((name) => tx.objectStore(name).clear()), tx.done]);
  return { cleared: true, pending: 0 };
}
